import {defineComponent, ref, onMounted, reactive, computed, watch} from '@vue/composition-api';
import CompanyChoice from '@/components/company-choice/company-choice.vue';
import type {AssignmentStatus} from '@/components/company-choice/company-choice';
import {validateCIN} from '@/functions/validation-functions';
import {SettingsContext} from "@/utilities/settings-context";
import axios from 'axios';
import { i18n } from "@/utilities/vue-i18n";
import {alertDialogue, generalError} from "@/functions/system-modal-functions";
import {createPageContext} from "@/components/navbar-box/page-context";
import TransitionSlide from "@/components/transition-slide/transition-slide.vue";
import {isAuthenticated} from "@/functions/authentication-functions";
import {validateEmail} from "@noordigitalagency/accounts-ledigajobb";

interface CompanyChoiceEntry { companyName: string; organizationNumber: string; limited: boolean; confirmed: boolean; assignmentStatus: AssignmentStatus }

const getParam = (param: unknown): string => typeof(param) === 'string' ? param : (Array.isArray(param) ? param.pop() : '');

export default defineComponent({

    components: {

        CompanyChoice,

        TransitionSlide
    },

    setup(_, context) {

        const grandidsession = getParam(context.root.$route.query.grandidsession);

        const token = getParam(context.root.$route.query.token);

        const target = getParam(context.root.$route.query.target);

        // eslint-disable-next-line
        // @ts-ignore
        const model = reactive({
            data: {
                owner: false, // NOTE: This flag says if a company owner who has received an "access request email", as the owner of the company, is going to give access to someone else
                staff: '',
                companies: new Array<CompanyChoiceEntry>()
            },
            email: '',
            cin: '',
            showCin: false,
            cinResults: {failed: false, results: null as unknown},
            companyType: computed(() => {
                if (!model.cin) return null;
                const result = validateCIN(model.cin);
                return result?.valid === true ? result.type : null as unknown as string;
            }),
            showCompanies: true,
            assigned: computed(() => {
                if (!model?.data?.companies) return false;
                return model.data.companies.some((value: CompanyChoiceEntry) => value.assignmentStatus.assigned) &&
                    model.data.companies.filter((value: CompanyChoiceEntry) => value.assignmentStatus.assigned).every((value: CompanyChoiceEntry) => value.assignmentStatus.agreed);
            }),
            searchOnly: false,
            canReset: false
        });

        const search = ref<HTMLElement | null>(null);

        const searchButton = ref<HTMLElement | null>(null);

        onMounted(async () => {

            try {
                if (grandidsession == null || typeof (grandidsession) === 'undefined') {
                    await context.root.$router.replace('/');
                }

                if (target && grandidsession === 'no' && token) {

                    const {redirectUrl} = await (await axios.get<{ redirectUrl: string }>(SettingsContext.getApiUrl('RequestBankId'), {
                        params: {
                            returnUrl: `${location.origin}/assignment?token=${token}&target=${target}`
                        }
                    })).data;

                    window.location.replace(redirectUrl);

                    return;
                }

                const result = (await axios.get(`${SettingsContext.getApiUrl('ownerships')}`, {

                    params: {
                        grandidsession,

                        token,

                        target
                    }

                })).data as { owner: boolean; staff: string; companies: Array<CompanyChoiceEntry> };

                model.data.owner = result.owner;

                model.data.staff = result.staff;

                model.data.companies = result.companies.map(value => ({...value,
                    assignmentStatus: {
                        administrator: !model.data.owner,
                        assigned: false,
                        agreed: false
                    } as AssignmentStatus
                }));

                model.showCompanies = model.data.companies.length > 0 || model.data.owner;
            }
            catch
            {
                await generalError(false);
            }
        });

        const submit = async (event: Event) => {

            event.preventDefault();

            const assignments = model.data.companies.filter((value: CompanyChoiceEntry) => value.assignmentStatus.assigned).map((value: CompanyChoiceEntry) => ({organizationNumber: value.organizationNumber, isAdministrator: value.assignmentStatus.administrator}));

            try {

                if(!token) {
                    await axios.post(`${SettingsContext.getApiUrl('assignSelf')}`, {
                        assignments,

                        target,

                        grandidsession,
                    });
                }
                else {
                    await axios.post(`${SettingsContext.getApiUrl('assign')}`, {

                        assignments,

                        token,

                        target
                    });
                }


                if (!model.data.owner) {

                    await alertDialogue(i18n.t('general.success') as string, i18n.t('pages.assignment.dialogue.companies-added') as string);

                    await context.root.$router.replace({name: 'Home', params: {needsReload: 'true'}});

                } else {

                    await alertDialogue(i18n.t('general.success') as string, i18n.t('pages.assignment.dialogue.companies-added-to', [model.data.staff]) as string);

                    window.location.href = SettingsContext.get('assignment.ownerNavigateUrl') as string;
                }
            }
            catch
            {
                await generalError(false);
            }
        };

        const showSearch = (event: Event) => {

            event.preventDefault();

            (search.value as HTMLElement).style.display = 'block';

            (searchButton.value as HTMLElement).style.display = 'none';
        };

        const searchCIN = async (event: Event) => {

            event.preventDefault();

            try{

                const results = (await axios.get(`${SettingsContext.getApiUrl('owners')}?token=${grandidsession}&cin=${model.cin}`)).data as Array<string>;

                model.cinResults.failed = false;

                model.cinResults.results = results;

                model.canReset = true;
            }
            catch {

                model.cinResults.results = null;

                model.cinResults.failed = true;
            }

            if (model.cinResults.results === '' || model.cinResults.results === [])
            {
                model.cin = '';
            }
        }

        const requestAssignment = async (event: Event) => {

            event.preventDefault();

            try {

                (await axios.post(`${SettingsContext.getApiUrl('requestAssign')}?token=${grandidsession}`, {email: model.email})).data;

                await alertDialogue(i18n.t('dialogue.request-sent') as string, i18n.t('dialogue.email-request-sent') as string);

                await context.root.$router.replace('/');
            }
            catch {

                await generalError(false);
            }
        };

        const resetSearch = () => {

            model.cin = null;

            model.cinResults.failed = false;

            model.cinResults.results = null;

            model.canReset = false;
        }

        const controls = ref();

        const pageContext = createPageContext({

            actions: [],

            breadcrumb: {showHome: computed(() => isAuthenticated() ? undefined : false) as unknown as (false|undefined), segments: [{text: !model.data.owner ? i18n.t('pages.assignment.add-business') as unknown as string : i18n.t('pages.assignment.access') as unknown as string, icon: '<i class="mdi mdi-connection"></i>'}]},

            title: {text: computed(() => !model.data.owner ? i18n.t('pages.assignment.add-business') : i18n.t('pages.assignment.access')) as unknown as string},

            controls
        });

        watch(() => model.data.owner, () => pageContext.breadcrumb.segments[0].text = !model.data.owner ? i18n.t('pages.assignment.add-business') as unknown as string : i18n.t('pages.assignment.access') as unknown as string);

        console.log(model.data.owner, i18n.t('pages.assignment.add-business') as unknown as string, i18n.t('pages.assignment.access') as unknown as string);

        return {

            submit,

            validateEmail,

            search,

            searchButton,

            showSearch,

            searchCIN,

            model,

            requestAssignment,

            controls,

            pageContext,

            resetSearch
        };
    }
});
